import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { brand } from "../../../config/theme";
import { cn } from "../../../components/ui";
import { useVehicles } from "../hooks/useVehicles";
import { useMxLabels } from "../hooks/useMxLabels";
import { useMaintenanceRecords } from "../hooks/useMaintenanceRecords";
import { useMaintenanceActions } from "../hooks/useMaintenanceActions";
import { useMaintenanceCapabilities } from "../hooks/useMaintenanceCapabilities";
import { generateMaintenanceReport } from "../services/maintenanceService";
import { CHART_AXIS, CATEGORY_ICONS, categoryOf } from "../theme";
import { costByMonth, costOf, dueOf, fmtDate, fmtMoney, fmtMonth, isOpen, isOverdue, lastMonths, photosOf, serviceDateOf, vehiclesInShop } from "../lib";
import type { MaintenanceRecord } from "../types";
import { DueText, ErrorBlock, KpiCard, LoadingBlock, PageShell, Panel, SearchInput, StatusBadge, ToolbarButton, TruncationNotice, TypeBadge } from "../components/kit/Primitives";
import { Photo, PhotoGallery, VehicleThumb } from "../components/kit/Media";
import { MaintenanceDrawer } from "../components/kit/MaintenanceDrawer";

/** Fecha de la línea de tiempo: lo abierto por su vencimiento, lo cerrado por su cierre. */
const refDate = (r: MaintenanceRecord) => (isOpen(r) ? dueOf(r) || r.openedAt || null : serviceDateOf(r));

// Historial por unidad: a la izquierda la flota (foto, servicios y costo
// acumulado; punto rojo si tiene algo vencido, ámbar si está en taller) y a la
// derecha la ficha de la unidad: KPIs, costo por mes y línea de tiempo de sus
// mantenimientos con evidencia. El PDF del historial sigue disponible.
export default function HistorialSection() {
  const L = useMxLabels();
  const { t } = L;
  const [params, setParams] = useSearchParams();
  const { capabilities } = useMaintenanceCapabilities();
  const { vehicles, vehicleOf } = useVehicles();
  const { rows, total: totalRecords, loading, loadingMore, error, reload, loadMore } = useMaintenanceRecords();
  const actions = useMaintenanceActions(reload);
  const [query, setQuery] = useState("");
  const [generating, setGenerating] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const summary = useMemo(() => {
    const shop = vehiclesInShop(rows);
    const byVehicle = new Map<string, MaintenanceRecord[]>();
    for (const r of rows) byVehicle.set(r.vehicleId, [...(byVehicle.get(r.vehicleId) || []), r]);
    const ids = new Set([...vehicles.map((v) => v.id), ...byVehicle.keys()]);
    return [...ids].map((id) => {
      const list = byVehicle.get(id) || [];
      return {
        id,
        count: list.length,
        cost: list.filter((r) => r.status === "completado").reduce((s, r) => s + costOf(r), 0),
        overdue: list.some((r) => isOverdue(r)),
        inShop: shop.has(id),
      };
    }).sort((a, b) => b.cost - a.cost);
  }, [rows, vehicles]);

  const vehicleId = params.get("vehicle") || summary[0]?.id || "";
  const setVehicle = (id: string) => setParams((p) => {
    const next = new URLSearchParams(p);
    next.set("vehicle", id);
    return next;
  }, { replace: true });

  const vehicle = vehicleOf(vehicleId);
  const records = useMemo(
    () => rows.filter((r) => r.vehicleId === vehicleId).sort((a, b) => Date.parse(refDate(b) || "") - Date.parse(refDate(a) || "")),
    [rows, vehicleId]
  );
  const done = records.filter((r) => r.status === "completado");
  const total = done.reduce((s, r) => s + costOf(r), 0);
  const last = done[0];
  const nextDue = records.filter(isOpen).map(dueOf).filter(Boolean).sort()[0] || null;
  const trend = useMemo(() => costByMonth(records, lastMonths(12)).map((m) => ({ ...m, label: fmtMonth(m.month, L.locale) })), [records, L.locale]);
  const selected = rows.find((r) => r.id === selectedId) || null;

  const visible = summary.filter((s) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    const v = vehicleOf(s.id);
    return [v?.label, v?.plate, v?.brand, v?.model].filter(Boolean).some((x) => String(x).toLowerCase().includes(q));
  });

  const handleReport = async () => {
    if (!vehicleId) return;
    setGenerating(true);
    try {
      const { url } = await generateMaintenanceReport({ kind: "history", vehicleId });
      window.open(url, "_blank");
    } catch {
      Swal.fire({ title: t("mantenimiento.reportError"), icon: "error", confirmButtonColor: brand.primary });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <PageShell
      icon="ri-history-line"
      title={t("mantenimiento.menu.historial")}
      subtitle={t("mantenimiento.historialSubtitle")}
      actions={
        <>
          <ToolbarButton icon="ri-refresh-line" label={t("mantenimiento.ui.refresh")} onClick={reload} spinning={loading} />
          <ToolbarButton primary icon={generating ? "ri-loader-4-line" : "ri-file-pdf-line"} spinning={generating} label={t("mantenimiento.downloadPdf")} onClick={handleReport} disabled={!vehicleId || generating} />
        </>
      }
      flush
    >
      <div className="flex h-full min-h-0 flex-col lg:flex-row">
        {/* Árbol de unidades */}
        <aside className="flex max-h-72 shrink-0 flex-col border-b border-line bg-surface lg:max-h-none lg:w-80 lg:border-b-0 lg:border-r">
          <div className="border-b border-line p-3">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-content-muted">{t("mantenimiento.ui.historial.fleet")} · {summary.length}</p>
            <SearchInput value={query} onChange={setQuery} placeholder={t("mantenimiento.ui.historial.searchVehicle")} className="max-w-none sm:max-w-none" />
          </div>
          <ul className="min-h-0 flex-1 overflow-y-auto p-2">
            {visible.map((s) => {
              const v = vehicleOf(s.id);
              const active = s.id === vehicleId;
              return (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => setVehicle(s.id)}
                    aria-current={active}
                    className={cn("flex w-full items-center gap-2.5 rounded-lg p-2 text-left transition-colors", active ? "bg-primary-500/10 ring-1 ring-primary-500/40" : "hover:bg-subtle")}
                  >
                    <VehicleThumb vehicle={v} size="sm" badge={s.overdue ? "alert" : s.inShop ? "shop" : null} />
                    <div className="min-w-0 flex-1">
                      <p className={cn("truncate text-sm", active ? "font-semibold text-primary-500" : "font-medium text-content")}>{v?.label ?? s.id}</p>
                      <p className="truncate text-[11px] text-content-muted">
                        {t("mantenimiento.ui.historial.services", { count: s.count })} · {fmtMoney(s.cost, L.locale, true)}
                      </p>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        {/* Ficha de la unidad */}
        <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
          {error ? (
            <ErrorBlock message={error} onRetry={reload} />
          ) : loading && !rows.length ? (
            <LoadingBlock />
          ) : !vehicleId ? (
            <div className="rounded-xl border border-dashed border-line bg-card p-10 text-center text-sm text-content-muted">
              <i className="ri-truck-line mb-2 block text-3xl" />
              {t("mantenimiento.selectVehiclePrompt")}
            </div>
          ) : (
            <div className="mx-auto max-w-[1300px] space-y-4">
              <TruncationNotice shown={rows.length} total={totalRecords} onLoadMore={loadMore} loading={loadingMore} />
              <div className="relative overflow-hidden rounded-xl border border-line bg-card">
                <div className="flex flex-col sm:flex-row">
                  <Photo src={vehicle?.image} alt={vehicle?.label || ""} fallbackIcon="ri-truck-line" className="h-48 w-full sm:h-auto sm:w-80" />
                  <div className="flex flex-1 flex-col justify-center gap-1 p-5">
                    <h2 className="text-xl font-semibold text-content">{vehicle?.label ?? vehicleId}</h2>
                    <p className="text-sm text-content-secondary">{[vehicle?.brand, vehicle?.model, vehicle?.year].filter(Boolean).join(" · ")}</p>
                    {vehicle?.plate ? (
                      <p className="mt-1 w-fit rounded-md border border-line bg-subtle px-2 py-0.5 font-mono text-xs text-content">
                        {t("mantenimiento.ui.historial.plate")}: {vehicle.plate}
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <KpiCard icon="ri-tools-line" tone="primary" label={t("mantenimiento.totalServices")} value={records.length} />
                <KpiCard icon="ri-money-dollar-circle-line" tone="success" label={t("mantenimiento.ui.historial.accumulated")} value={fmtMoney(total, L.locale)} />
                <KpiCard icon="ri-checkbox-circle-line" tone="info" label={t("mantenimiento.ui.historial.lastService")} value={<span className="text-lg">{last ? fmtDate(serviceDateOf(last), L.locale) : "—"}</span>} hint={last?.maintenanceDescription} />
                <KpiCard icon="ri-calendar-event-line" tone={records.some((r) => isOverdue(r)) ? "error" : "warning"} label={t("mantenimiento.ui.historial.nextDue")} value={<span className="text-lg">{nextDue ? fmtDate(nextDue, L.locale) : "—"}</span>} />
              </div>

              <Panel title={t("mantenimiento.ui.historial.costTrend")} icon="ri-line-chart-line">
                <div className="h-48">
                  <ResponsiveContainer>
                    <AreaChart data={trend} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="mxTrend" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.45} />
                          <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={CHART_AXIS.grid} vertical={false} />
                      <XAxis dataKey="label" tick={CHART_AXIS.tick} axisLine={false} tickLine={false} />
                      <YAxis tick={CHART_AXIS.tick} axisLine={false} tickLine={false} width={60} tickFormatter={(v) => fmtMoney(Number(v), L.locale, true)} />
                      <Tooltip contentStyle={CHART_AXIS.tooltip} formatter={(v: number) => fmtMoney(Number(v), L.locale)} />
                      <Area isAnimationActive={false} type="monotone" dataKey="total" name={t("mantenimiento.ui.charts.cost")} stroke="#3b82f6" strokeWidth={2} fill="url(#mxTrend)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </Panel>

              <Panel title={t("mantenimiento.ui.drawer.timeline")} icon="ri-git-commit-line">
                {records.length === 0 ? (
                  <p className="py-6 text-center text-sm text-content-muted">{t("mantenimiento.ui.historial.noRecords")}</p>
                ) : (
                  <ol className="relative ml-2 border-l-2 border-line">
                    {records.map((r) => {
                      const cat = categoryOf(r.maintenanceDescription);
                      const photos = photosOf(r);
                      return (
                        <li key={r.id} className="mb-4 pl-5">
                          <span className="absolute -left-[13px] flex h-6 w-6 items-center justify-center rounded-full bg-card ring-2 ring-line">
                            <i className={cn(CATEGORY_ICONS[cat], "text-xs text-content-secondary")} />
                          </span>
                          <button type="button" onClick={() => setSelectedId(r.id)} className="w-full rounded-xl border border-line bg-surface p-3 text-left transition-colors hover:bg-subtle/40">
                            <div className="flex flex-wrap items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="text-xs text-content-muted">{fmtDate(refDate(r), L.locale)} · {r.workshopName || L.mode(r.workshopMode)}</p>
                                <p className="text-sm font-medium text-content">{r.maintenanceDescription}</p>
                              </div>
                              <span className="text-sm font-semibold tabular-nums text-content">{costOf(r) ? fmtMoney(costOf(r), L.locale) : "—"}</span>
                            </div>
                            <div className="mt-2 flex flex-wrap items-center gap-1.5">
                              <StatusBadge record={r} size="sm" />
                              <TypeBadge type={r.type} />
                              <DueText record={r} />
                            </div>
                            {photos.length ? <PhotoGallery photos={photos} alt={r.maintenanceDescription || ""} layout="strip" max={5} className="mt-2" /> : null}
                          </button>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </Panel>
            </div>
          )}
        </div>
      </div>

      {selected ? (
        <MaintenanceDrawer
          record={selected}
          vehicle={vehicleOf(selected.vehicleId)}
          onClose={() => setSelectedId(null)}
          canManage={capabilities.canManageRecords}
          canCharge={capabilities.canCharge}
          onTransition={actions.transition}
          onPdf={actions.workOrderPdf}
          onCharge={actions.charge}
        />
      ) : null}
    </PageShell>
  );
}
